import React from 'react';
import { Badge, Button, Card } from '@parsina/framework';
import {
  TriangleAlert, IdCard, ClipboardList, Pill, FlaskConical, Stethoscope, MessagesSquare,
} from 'lucide-react';

import { C, CT } from '../../../constants/theme';
import { useTranslation } from '../../../i18n/I18nProvider.jsx';
import { ageLabel } from '../../../lib/caseForm.js';
import {
  dxKindKey, urgencyKey, orderTypeKey, encounterStatusKey,
} from '../../../constants/vocabulary.js';
import RecordSection, { ViewAllButton } from '../../../components/common/RecordSection.jsx';
import RecordListSheet from '../../../components/common/RecordListSheet.jsx';
import { allergyEntries, recentThreads, latestVisit, firstLine } from '../PatientRecord.shared.js';

// خلاصه — the record at a glance. core.md §7.4.
//
// Everything here except «مشخصات» is DERIVED BY THE BFF (core.md §6) and never
// recomputed in the browser: active problems are de-duplicated across every
// visit, current medications fold the latest visit's prescription together with
// the `medications` history section, pending orders span visits. A client that
// derived them itself would disagree with the prompt the assistant is reading,
// which is the one disagreement a teaching app cannot afford.
//
// The blocks are `RecordSection`s, so a student who never reads «دستورهای در
// انتظار» collapses it once and it stays collapsed. «هشدار حساسیت» is the
// exception: it is locked open and rendered first, whenever there is one.

const LIST_HEAD = 3;

const Line = ({ label, value }) => {
  const { t } = useTranslation();
  const empty = value == null || value === '';
  return (
    <div style={{ display: 'flex', gap: 10, padding: '5px 0', fontSize: 12.5 }}>
      <span style={{ color: CT.mute, minWidth: 96 }}>{label}</span>
      <span style={{ color: empty ? CT.mute : C.textPrimary, overflowWrap: 'anywhere' }}>
        {empty ? t('common.labels.none') : value}
      </span>
    </div>
  );
};

const Row = ({ children, testId }) => (
  <div
    data-testid={testId}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 7,
      flexWrap: 'wrap',
      padding: '7px 0',
      borderBottom: `1px solid ${C.border}`,
      fontSize: 13,
      color: C.textPrimary,
    }}
  >
    {children}
  </div>
);

const Nothing = ({ children }) => (
  <p style={{ margin: '6px 0', fontSize: 12.5, color: CT.mute }}>{children}</p>
);

export default function OverviewTab({
  caseRow,
  entries,
  activeProblems,
  currentMedications,
  pendingOrders,
  encounters,
  threads,
  onOpenVisit,
  onOpenThread,
}) {
  const { t, formatNumber, formatDate } = useTranslation();
  const [sheet, setSheet] = React.useState(null); // 'problems' | 'meds' | 'orders' | null

  const allergies = allergyEntries(entries);
  const last = latestVisit(encounters);
  const chats = recentThreads(threads);

  const problemRow = (p, i) => (
    <Row key={`${p.title}-${i}`} testId="overview-problem">
      <span style={{ fontWeight: 700 }}>{p.title}</span>
      {p.icdCode && <span dir="ltr" style={{ fontSize: 11, color: CT.mute }}>{p.icdCode}</span>}
      {p.kind && <Badge variant="outline">{t(dxKindKey(p.kind))}</Badge>}
      {p.encounterSeq != null && (
        <span style={{ marginInlineStart: 'auto', fontSize: 11, color: CT.mute }}>
          {t('record.overview.fromVisit', { n: formatNumber(p.encounterSeq) })}
        </span>
      )}
    </Row>
  );

  const medRow = (m, i) => (
    <Row key={`${m.name}-${i}`} testId="overview-med">
      <span style={{ fontWeight: 700 }}>{m.name}</span>
      {m.detail && <span style={{ fontSize: 12, color: CT.mute }}>{m.detail}</span>}
      <span style={{ marginInlineStart: 'auto', fontSize: 11, color: CT.mute }}>
        {/* Where the row came from: a prescription in visit N, or the شرح حال
            section the student wrote outside any visit. Both are "current
            medications" and the student has to be able to tell them apart. */}
        {m.encounterSeq != null
          ? t('record.overview.fromVisit', { n: formatNumber(m.encounterSeq) })
          : t('record.overview.fromHistory')}
      </span>
    </Row>
  );

  const orderRow = (o) => (
    <Row key={o.id} testId="overview-order">
      <span style={{ fontWeight: 700 }}>{o.title}</span>
      <Badge variant="outline">{t(orderTypeKey(o.orderType))}</Badge>
      {o.urgency && o.urgency !== 'routine' && (
        <Badge variant="warning">{t(urgencyKey(o.urgency))}</Badge>
      )}
    </Row>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="record-overview">
      {/* Allergies FIRST and never collapsible: this is the one block whose
          absence from a student's attention is a clinical error, and a safety
          notice that folds away is one they fold away once. */}
      {allergies.length > 0 && (
        <RecordSection
          id="allergies"
          title={t('record.overview.allergies')}
          icon={TriangleAlert}
          locked
          danger
          count={allergies.length}
          data-testid="record-allergies"
        >
          {allergies.map((entry) => (
            <p
              key={entry.id}
              style={{ margin: '4px 0', fontSize: 13, color: C.textPrimary, whiteSpace: 'pre-wrap' }}
            >
              {entry.label ? `${entry.label}: ` : ''}{entry.body}
            </p>
          ))}
        </RecordSection>
      )}

      <RecordSection id="details" title={t('record.overview.details')} icon={IdCard}>
        <Line label={t('caseForm.fields.title')} value={caseRow?.title} />
        <Line label={t('caseForm.fields.displayName')} value={caseRow?.displayName} />
        <Line label={t('practice.caseRef.label')} value={caseRow?.caseRef} />
        <Line label={t('caseForm.fields.ageYears')} value={ageLabel(caseRow, t)} />
        <Line
          label={t('caseForm.fields.sex')}
          value={caseRow?.sex ? t(`common.gender.${caseRow.sex}`) : ''}
        />
        <Line label={t('caseForm.fields.bloodType')} value={caseRow?.bloodType} />
        <Line
          label={t('caseForm.fields.weightKg')}
          value={caseRow?.weightKg ? `${formatNumber(caseRow.weightKg)} ${t('common.units.kg')}` : ''}
        />
        <Line
          label={t('caseForm.fields.heightCm')}
          value={caseRow?.heightCm ? `${formatNumber(caseRow.heightCm)} ${t('common.units.cm')}` : ''}
        />
        <Line label={t('caseForm.fields.chiefComplaint')} value={caseRow?.chiefComplaint} />
        <Line label={t('caseForm.fields.note')} value={caseRow?.note} />
      </RecordSection>

      <RecordSection
        id="problems"
        title={t('record.overview.problems')}
        icon={ClipboardList}
        count={activeProblems.length}
        action={activeProblems.length > LIST_HEAD
          ? <ViewAllButton onClick={() => setSheet('problems')} testId="problems-view-all" />
          : null}
      >
        {activeProblems.length
          ? activeProblems.slice(0, LIST_HEAD).map(problemRow)
          : <Nothing>{t('record.overview.noProblems')}</Nothing>}
      </RecordSection>

      <RecordSection
        id="meds"
        title={t('record.overview.meds')}
        icon={Pill}
        count={currentMedications.length}
        action={currentMedications.length > LIST_HEAD
          ? <ViewAllButton onClick={() => setSheet('meds')} testId="meds-view-all" />
          : null}
      >
        {currentMedications.length
          ? currentMedications.slice(0, LIST_HEAD).map(medRow)
          : <Nothing>{t('record.overview.noMeds')}</Nothing>}
      </RecordSection>

      <RecordSection
        id="pendingOrders"
        title={t('record.overview.pendingOrders')}
        icon={FlaskConical}
        count={pendingOrders.length}
        action={pendingOrders.length > LIST_HEAD
          ? <ViewAllButton onClick={() => setSheet('orders')} testId="orders-view-all" />
          : null}
      >
        {pendingOrders.length
          ? pendingOrders.slice(0, LIST_HEAD).map(orderRow)
          : <Nothing>{t('record.overview.noOrders')}</Nothing>}
      </RecordSection>

      <RecordSection id="lastVisit" title={t('record.overview.lastVisit')} icon={Stethoscope}>
        {last ? (
          <Card style={{ padding: 12 }} data-testid="overview-last-visit">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 800, fontSize: 13.5 }}>
                {t('visit.number', { n: formatNumber(last.seq) })}
              </span>
              <Badge variant={last.status === 'open' ? 'warning' : 'outline'}>
                {t(encounterStatusKey(last.status))}
              </Badge>
              <span style={{ fontSize: 11.5, color: CT.mute }}>
                {formatDate(last.startedAtMs, 'numeric')}
              </span>
              <Button
                size="sm"
                variant="outline"
                style={{ marginInlineStart: 'auto' }}
                onClick={() => onOpenVisit?.(last)}
                data-testid="overview-open-visit"
              >
                {t('record.overview.openVisit')}
              </Button>
            </div>
            {last.reason && (
              <p style={{ margin: '8px 0 0', fontSize: 12.5, color: CT.mute }}>{last.reason}</p>
            )}
            {/* core.md §7.4: the summary AND the first line of «A», each on
                its own line, when both exist — the summary is the visit in
                one sentence, the A line is what was concluded, and neither
                stands in for the other. */}
            {firstLine(last.summary) && (
              <p
                style={{ margin: '6px 0 0', fontSize: 13, color: C.textPrimary }}
                data-testid="overview-last-visit-summary"
              >
                {firstLine(last.summary)}
              </p>
            )}
            {firstLine(last.note?.a) && (
              <p
                style={{ margin: '4px 0 0', fontSize: 13, color: C.textPrimary }}
                data-testid="overview-last-visit-assessment"
              >
                {firstLine(last.note?.a)}
              </p>
            )}
          </Card>
        ) : (
          <Nothing>{t('record.overview.noVisits')}</Nothing>
        )}
      </RecordSection>

      <RecordSection
        id="recentChats"
        title={t('record.overview.recentChats')}
        icon={MessagesSquare}
        count={chats.length}
      >
        {chats.length ? chats.map((thread) => (
          <Row key={thread.id} testId="overview-thread">
            <button
              type="button"
              onClick={() => onOpenThread?.(thread)}
              style={{
                background: 'none',
                border: 0,
                padding: 0,
                font: 'inherit',
                color: C.teal,
                cursor: 'pointer',
                textAlign: 'start',
              }}
            >
              {thread.title || t('record.overview.recentChats')}
            </button>
            <span style={{ marginInlineStart: 'auto', fontSize: 11, color: CT.mute }}>
              {/* The SERVER's count. Thread rows arrive with `messages` empty by
                  contract, so `.length` here would always print zero. */}
              {formatNumber(Number(thread.messageCount || 0))}
            </span>
          </Row>
        )) : <Nothing>{t('record.overview.noChats')}</Nothing>}
      </RecordSection>

      <RecordListSheet
        open={sheet === 'problems'}
        onClose={() => setSheet(null)}
        title={t('record.overview.problems')}
      >
        {activeProblems.map(problemRow)}
      </RecordListSheet>
      <RecordListSheet
        open={sheet === 'meds'}
        onClose={() => setSheet(null)}
        title={t('record.overview.meds')}
      >
        {currentMedications.map(medRow)}
      </RecordListSheet>
      <RecordListSheet
        open={sheet === 'orders'}
        onClose={() => setSheet(null)}
        title={t('record.overview.pendingOrders')}
      >
        {pendingOrders.map(orderRow)}
      </RecordListSheet>
    </div>
  );
}
